/* eslint-disable check-preloaders/no-promise-all */
import { getLogger } from '@confluence/logger';

import { defer } from './defer.ts';
import { startPreload } from './deferredStart.ts';
import {
	LOADING_TIMEOUT,
	REACT_LAZY_ID_PREFIX,
	getReportedIds,
	reportLoadableTimeouts,
} from './loadableGlobals.ts';

const logger = getLogger('Loadable');

let resolveMap: {
	[nameOrId: string]: { resolve: () => void; promise: Promise<void>; cancel: () => void };
} = {};
let loadablesInitialized = false;

function initializeLoadable() {
	if (loadablesInitialized) return;
	loadablesInitialized = true;

	for (const nameOrId of getReportedIds()) {
		// A Loadable that hydrates before its split has loaded renders its loading state and fails
		// hydration, so preloadLoadable waits for it. A React.lazy component suspends instead, and
		// its module may only run during hydration, so it is preloaded but not waited for.
		if (!nameOrId.startsWith(REACT_LAZY_ID_PREFIX)) {
			resolveMap[nameOrId] = defer(LOADING_TIMEOUT, () => {
				logger.error`Loadable initialization timed out: ${nameOrId}`;
				(window.__LOADABLE_TIMEOUTS__ ||= []).push(nameOrId);
			});
		}
	}
}

export async function preloadLoadable(): Promise<any> {
	initializeLoadable();
	await Promise.all(Object.values(resolveMap).map(({ promise }) => promise));
	reportLoadableTimeouts();
}

export function preloadIfNeeded(
	id: string | undefined,
	name: string | undefined,
	preload: () => Promise<any>,
): void {
	initializeLoadable();

	// mustPreloadWhenRenderingSPA can list a loadable under both its id and its name
	const keys = [id, name].filter(
		(nameOrId): nameOrId is string => !!nameOrId && getReportedIds().has(nameOrId),
	);
	if (!keys.length) return;

	const resolve = () => keys.forEach((key) => resolveMap[key]?.resolve());
	startPreload(() => {
		const startTime = performance.now();
		void preload().then(
			() => {
				logger.debug`SSR Preload: ${name || id} in ${Math.floor(performance.now() - startTime)}ms`;
				resolve();
			},
			(e) => {
				logger.error(e);
				resolve();
			},
		);
	});
}

export function _resetPreloaderV2OnlyForTest(): void {
	resolveMap = {};
	loadablesInitialized = false;
}
